import { useState } from 'react';

import { IconClose } from '../../../common/icons';
import { useSgCow } from '../contexts/cow';
import { SG_FEATURES, SG_FEATURE_BY_KEY, fmtDate, val, valueText } from '../utils/features';
import SgIdentities from './identities';
import SgHistoryTab from './history-tab';
import SgValue from './value';

import '../styles/sg.css';

// Detalle por VACA FÍSICA (admin-sg.md · B2): cabecera cowre (SoT) + línea temporal de
// identidades + tabs internos (Resumen "último-X" agregado sobre todas las fichas + un
// historial por feature, B3). Se monta con key={cowId} desde el navegador (cows.jsx),
// así el tab activo y el historial se reinician al cambiar de vaca (patrón v1).

const SEX_LABEL = { H: 'Hembra', M: 'Macho', female: 'Hembra', male: 'Macho' };

export function FeatureCard({ def, entry }) {
    const { card, Icon } = def;
    return (
        <div className={`sg-feat ${entry ? '' : 'sg-feat--empty'}`}>
            <div className="sg-feat__head"><Icon /> {card.title}</div>
            {entry ? (
                <>
                    <div className="sg-feat__big" title={valueText(card.big(entry))}>{card.big(entry)}</div>
                    <div className="sg-feat__sub"><SgValue value={card.sub(entry)} /></div>
                    <dl className="sg-feat__rows">
                        {card.rows(entry).map(([k, v, cls]) => (
                            <div key={k} className="sg-feat__row">
                                <dt>{k}</dt>
                                {/* `valueText` y no String(v): un marcador daría "[object Object]" en el title. */}
                                <dd className={cls === 'mono' ? 'sg-feat__value--mono' : undefined} title={valueText(v)}>
                                    <SgValue value={v} />
                                </dd>
                            </div>
                        ))}
                    </dl>
                </>
            ) : (
                <div className="sg-feat__none">Sin registro</div>
            )}
        </div>
    );
}

export default function SgCowDetail({ cowId, onClose }) {
    const { data, isLoading, error } = useSgCow(cowId);
    const [tab, setTab] = useState('resumen');

    if (isLoading) return <div className="sg-detail"><div className="sg-detail__state">Cargando detalle…</div></div>;
    if (error) {
        return (
            <div className="sg-detail">
                <div className="sg-detail__error">
                    {error.status === 404
                        ? 'Esta vaca ya no está disponible (inactiva o sin fichas conciliadas).'
                        : (error.message || 'No se pudo cargar el detalle de la vaca')}
                </div>
            </div>
        );
    }
    if (!data) return null;

    const cow = data.cow || {};
    const activeFeature = tab !== 'resumen' ? SG_FEATURE_BY_KEY[tab] : null;

    return (
        <div className="sg-detail">
            <header className="sg-detail__head">
                <div className="sg-detail__heading">
                    <h2 className="sg-detail__name">
                        {cow.name || <span className="sg-detail__name--mono">#{val(cow.number)}</span>}
                    </h2>
                    <div className="sg-detail__meta">
                        <span className="sg-detail__chip">N° <b>{val(cow.number)}</b></span>
                        <span className="sg-detail__chip">Registro <b>{val(cow.register)}</b></span>
                        <span className="sg-detail__chip">Sexo <b>{SEX_LABEL[cow.sex] || val(cow.sex)}</b></span>
                        <span className="sg-detail__chip">Nacim. <b>{fmtDate(cow.birth)}</b></span>
                        <span className="sg-detail__chip">Fichas SG <b>{data.sg_count ?? 0}</b></span>
                    </div>
                </div>
                {onClose && (
                    <button type="button" className="sg-detail__close" onClick={onClose} aria-label="Cerrar detalle"><IconClose /></button>
                )}
            </header>

            <SgIdentities identities={data.identities} />

            <nav className="sg-tabs" role="tablist" aria-label="Vistas de la vaca">
                <button
                    type="button" role="tab" aria-selected={tab === 'resumen'}
                    className={`sg-tab ${tab === 'resumen' ? 'sg-tab--active' : ''}`}
                    onClick={() => setTab('resumen')}
                >
                    Resumen
                </button>
                {SG_FEATURES.map((f) => (
                    <button
                        key={f.key} type="button" role="tab" aria-selected={tab === f.key}
                        className={`sg-tab ${tab === f.key ? 'sg-tab--active' : ''}`}
                        onClick={() => setTab(f.key)}
                    >
                        <f.Icon /> {f.label}
                    </button>
                ))}
            </nav>

            {tab === 'resumen' ? (
                <div className="sg-feats">
                    {SG_FEATURES.map((f) => (
                        <FeatureCard key={f.key} def={f} entry={data.last_events?.[f.key] || null} />
                    ))}
                </div>
            ) : (
                <SgHistoryTab cowId={cowId} feature={activeFeature} />
            )}
        </div>
    );
}
